import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    FormControlLabel,
    IconButton,
    Link as MuiLink,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Slider,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowBack,
    AutoAwesome,
    CloudUpload,
    ExpandLess,
    ExpandMore,
    Image as ImageIcon,
    InfoOutlined,
    Refresh,
    Save,
} from '@mui/icons-material';
import {uploadApi} from '../../api/uploadApi';
import {AiImageMode, AiImageProvider} from '../../types/aiImage';
import {
    BACKGROUND_KIND_LABEL_KO,
    BACKGROUND_KINDS,
    BackgroundKind,
    BubbleTailDir,
    BUBBLE_TAIL_DIR_LABEL_KO,
    COSTUME_SIZE_PRESETS,
    CostumeSizePreset,
    PatchCostumeRequest,
    PLACEMENT_TYPE_LABEL_KO,
    PLACEMENT_TYPES,
    PlacementType,
    RequestHistoryItem,
    SIZE_PRESET_LABEL_KO,
    SIZE_PRESET_TILE_MAP,
    TextBubbleMeta,
} from '../../types/eppiCostume';
import {
    CostumeCategory,
    CostumeRenderKind,
    RENDER_KIND_LABEL_KO,
} from '../../types/costumeCategory';
import {useCostumeCategories} from '../../hooks/useCostumeCategory';
import {
    useBubbleStyleCatalog,
    useEppiCostumeDetail,
    useEppiCostumeHistory,
    useEppiCostumes,
    useFinalizeEppiCostume,
    useGenerateEppiCostume,
    usePatchEppiCostume,
} from '../../hooks/useEppiCostumes';
import {
    ALL_BUBBLE_META_KEYS,
    BUBBLE_OPTIONAL_FIELDS,
    BUBBLE_PAD_FIELDS,
    BUBBLE_TEXT_FIELDS,
    bubbleMetaFromDetail,
    emptyBubbleMeta,
    isBubbleMetaComplete,
} from './bubbleMetaFields';
import {RequestHistoryCard} from './RequestHistoryCard';

const CODE_REGEX = /^[A-Z][A-Z0-9_]*$/;
/** V200 painter — bubble_color_hex 형식 검증. 백엔드 5031 과 동일. */
const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

interface SectionProps {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}

const Section: FC<SectionProps> = ({title, subtitle, children}) => (
    <Paper sx={{p: 3, mb: 2}} elevation={1}>
        <Stack spacing={1} sx={{mb: 2}}>
            <Typography variant="h6" fontWeight={700}>
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            )}
        </Stack>
        {children}
    </Paper>
);

export const EppiCostumeFormPage: FC = () => {
    const navigate = useNavigate();
    const {code: codeParam} = useParams<{code?: string}>();
    const isEditMode = !!codeParam;

    // 리스트 — slotGroups + nextSortOrder 산정용
    const listQuery = useEppiCostumes();
    // 카테고리 목록 — sortOrder ASC 정렬 후 select / default 결정
    const categoriesQuery = useCostumeCategories();
    const categories: CostumeCategory[] = useMemo(
        () =>
            [...(categoriesQuery.data ?? [])].sort(
                (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
            ),
        [categoriesQuery.data]
    );
    // ARTIFACT는 placementType별로 row가 2개이므로 (renderKind, placementType) 합성 키로 매핑
    const slotGroupMap = useMemo(() => {
        const groups = listQuery.data?.slotGroups ?? [];
        const map: Record<string, (typeof groups)[number]> = {};
        for (const g of groups) {
            const key = g.placementType ? `${g.renderKind}:${g.placementType}` : g.renderKind;
            map[key] = g;
        }
        return map;
    }, [listQuery.data?.slotGroups]);

    // 편집 모드: 상세 prefetch
    const detailQuery = useEppiCostumeDetail(isEditMode ? codeParam : null);
    const detail = detailQuery.data;

    // ── 섹션 1. 메타 폼 상태 ────────────────────────────────────────────────
    const [code, setCode] = useState('');
    /** V201: 카테고리 id (선택 전 null). 선택된 카테고리의 renderKind 가 후속 분기를 결정. */
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const selectedCategory = useMemo(
        () => categories.find((c) => c.id === categoryId) ?? null,
        [categories, categoryId]
    );
    const renderKind: CostumeRenderKind | null = selectedCategory?.renderKind ?? null;
    const [nameKo, setNameKo] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [nameJa, setNameJa] = useState('');
    const [nameVi, setNameVi] = useState('');
    const [priceXp, setPriceXp] = useState<number | ''>(1000);
    const [sortOrder, setSortOrder] = useState<number | ''>(1);
    const [isActive, setIsActive] = useState(true);
    // BACKGROUND 슬롯 전용 — 다른 슬롯에서는 무시.
    const [bgKind, setBgKind] = useState<BackgroundKind>('IMAGE');
    const [bgColor, setBgColor] = useState('');
    const [bgTileSizePx, setBgTileSizePx] = useState<number | ''>('');
    // ARTIFACT 슬롯 전용 — 다른 슬롯에서는 무시.
    const [sizePreset, setSizePreset] = useState<CostumeSizePreset>('SMALL');
    const [placementType, setPlacementType] = useState<PlacementType>('FLOOR');
    const [isRoomEditorVisible, setIsRoomEditorVisible] = useState(false);
    // TEXT_BUBBLE 슬롯 전용 — 다른 슬롯에서는 무시. 숫자 필드를 '' 빈값 허용으로 보관.
    // V200 painter: slice_* 4필드는 입력 안 받음(빈값 유지 → payload 미전송).
    const [bubbleMeta, setBubbleMeta] = useState<
        Record<keyof TextBubbleMeta, number | ''>
    >(() => emptyBubbleMeta());
    const setBubbleField = (key: keyof TextBubbleMeta, raw: string) =>
        setBubbleMeta((prev) => ({
            ...prev,
            [key]: raw === '' ? '' : Number(raw),
        }));
    // V200 painter — bubble_style_code / bubble_color_hex / bubble_tail_dir.
    // 카탈로그 로드 후 첫 번째 style 로 default. style 변경 시 tail_dir 도 supportedTailDirs[0] 로 재설정.
    const [bubbleStyleCode, setBubbleStyleCode] = useState<string>('');
    const [bubbleColorHex, setBubbleColorHex] = useState<string>('');
    const [bubbleTailDir, setBubbleTailDir] = useState<BubbleTailDir>('LEFT');
    const bubbleStylesQuery = useBubbleStyleCatalog();
    // useEffect 의존성 안정화를 위해 ref equal 유지
    const bubbleStyles = useMemo(
        () => bubbleStylesQuery.data ?? [],
        [bubbleStylesQuery.data]
    );
    const activeBubbleStyle = bubbleStyles.find((s) => s.code === bubbleStyleCode);
    // 편집 모드에서 preset/placement 가 변경됐는지 시각 안내용
    const [presetWarning, setPresetWarning] = useState<string | null>(null);
    // V190: 시스템 기본 코스튬 토글 (편집 모드 CHARACTER/BACKGROUND 슬롯 전용)
    const [isDefault, setIsDefault] = useState(false);
    const [defaultToggleError, setDefaultToggleError] = useState<string | null>(null);

    // 카테고리에 해당하는 sortOrder default = 같은 categoryId 최대 + 1
    const nextSortOrder = useMemo(() => {
        if (categoryId == null) return 1;
        const items = (listQuery.data?.items ?? []).filter(
            (c) => c.categoryId === categoryId
        );
        if (items.length === 0) return 1;
        return Math.max(...items.map((c) => c.sortOrder)) + 1;
    }, [listQuery.data?.items, categoryId]);

    // renderKind 별 그룹 메타 (systemContext preview + base URL 동기화 effect 가 사용)
    // ARTIFACT는 placementType에 따라 BACK_WALL/FLOOR row 중 매칭
    const activeGroup =
        renderKind === 'ARTIFACT'
            ? slotGroupMap[`ARTIFACT:${placementType}`]
            : renderKind
            ? slotGroupMap[renderKind]
            : null;
    const systemContext = activeGroup?.systemContext;

    // ── 섹션 3. AI 옵션 상태 ────────────────────────────────────────────────
    const [defaultPrompt, setDefaultPrompt] = useState('');
    const [provider, setProvider] = useState<AiImageProvider>('GEMINI');
    const [mode, setMode] = useState<AiImageMode>('SYNC');
    const [imageCount, setImageCount] = useState(4);
    const [baseImageUrl, setBaseImageUrl] = useState('');

    // ── 섹션 4. 결과 상태 ───────────────────────────────────────────────────
    // 선택된 result 는 (requestId, resultId) 쌍으로 식별 — 여러 request 간 교차 선택 가능
    const [selectedRequestId, setSelectedRequestId] = useState<number | null>(
        null
    );
    const [selectedResultId, setSelectedResultId] = useState<number | null>(null);

    // systemContext readonly preview 카드 펼침 상태 (기본 접힘)
    const [systemContextOpen, setSystemContextOpen] = useState(false);

    const [metaError, setMetaError] = useState<string | null>(null);
    const [generateError, setGenerateError] = useState<string | null>(null);
    const [finalizeError, setFinalizeError] = useState<string | null>(null);
    const [metaSaved, setMetaSaved] = useState(false);
    const [finalizeSuccess, setFinalizeSuccess] = useState(false);

    // 직접 이미지 업로드 상태 (편집 모드 전용)
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const generateMut = useGenerateEppiCostume();
    const finalizeMut = useFinalizeEppiCostume();
    const patchMut = usePatchEppiCostume();

    // history 로딩: 편집 모드는 codeParam, 신규 모드는 generate 응답으로 code 가 결정된 후
    const historyCode = isEditMode ? codeParam ?? null : code || null;
    const historyQuery = useEppiCostumeHistory(historyCode);
    const history: RequestHistoryItem[] = historyQuery.data ?? [];

    // ── prefill (편집 모드 detail 로드 후 / 신규 모드 nextSortOrder 산정 후) ─
    useEffect(() => {
        if (isEditMode) {
            if (!detail) return;
            setCode(detail.code);
            setCategoryId(detail.categoryId);
            setNameKo(detail.nameKo);
            setNameEn(detail.nameEn);
            setNameJa(detail.nameJa);
            setNameVi(detail.nameVi);
            setDefaultPrompt(detail.defaultPrompt ?? '');
            setPriceXp(detail.priceXp);
            setSortOrder(detail.sortOrder);
            setIsActive(detail.isActive);
            setBgKind(detail.bgKind ?? 'IMAGE');
            setBgColor(detail.bgColor ?? '');
            setBgTileSizePx(detail.bgTileSizePx ?? '');
            // ARTIFACT 슬롯 — 서버 값 그대로 prefill (없으면 default)
            setSizePreset(detail.sizePreset ?? 'SMALL');
            setPlacementType(detail.placementType ?? 'FLOOR');
            setIsRoomEditorVisible(detail.isRoomEditorVisible ?? false);
            setIsDefault(detail.isDefault ?? false);
            // TEXT_BUBBLE 메타 — 서버 값 prefill (null → '')
            setBubbleMeta(bubbleMetaFromDetail(detail));
            // V200 painter — 서버 값 prefill (없으면 빈 문자열/LEFT).
            setBubbleStyleCode(detail.bubbleStyleCode ?? '');
            setBubbleColorHex(detail.bubbleColorHex ?? '');
            setBubbleTailDir((detail.bubbleTailDir as BubbleTailDir) ?? 'LEFT');
            setPresetWarning(null);
            setDefaultToggleError(null);
        } else {
            // 신규 모드 — nextSortOrder 기본값 (categoryId 변경 시도 재산정)
            setSortOrder((prev) => (prev === '' ? nextSortOrder : prev));
        }
        // baseImageUrl 은 defaultBaseImageUrl 가 로드되면 prefill (수동 입력 보존)
    }, [isEditMode, detail, nextSortOrder]);

    // 신규 모드 — 카테고리 로드 완료 후 첫 번째 시드 카테고리(sortOrder 가장 작은 system)를 기본 선택.
    // categoriesQuery 가 늦게 로드되면 placeholder 표시 → 로드 후 system 카테고리로 prefill.
    useEffect(() => {
        if (isEditMode) return;
        if (categoryId != null) return;
        if (categories.length === 0) return;
        const firstSystem = categories.find((c) => c.isSystem) ?? categories[0];
        setCategoryId(firstSystem.id);
    }, [isEditMode, categoryId, categories]);

    // V200 painter — TEXT_BUBBLE 슬롯 + 카탈로그 로드 후 default style 채움 (신규 모드 한정).
    // 편집 모드에서는 detail prefill 효과가 이미 채웠으므로 덮어쓰지 않음.
    useEffect(() => {
        if (renderKind !== 'TEXT_BUBBLE') return;
        if (isEditMode) return;
        if (bubbleStyleCode) return;
        if (bubbleStyles.length === 0) return;
        const first = bubbleStyles[0];
        setBubbleStyleCode(first.code);
        // tail dir 도 새 style 의 supportedTailDirs[0] 로 일치
        if (!first.supportedTailDirs.includes(bubbleTailDir)) {
            setBubbleTailDir(first.supportedTailDirs[0] ?? 'LEFT');
        }
    }, [renderKind, isEditMode, bubbleStyles, bubbleStyleCode, bubbleTailDir]);

    // style 변경 시 새 style 이 현재 tail_dir 을 지원하지 않으면 첫 번째 지원값으로 강제.
    useEffect(() => {
        if (!activeBubbleStyle) return;
        if (!activeBubbleStyle.supportedTailDirs.includes(bubbleTailDir)) {
            setBubbleTailDir(
                activeBubbleStyle.supportedTailDirs[0] ?? 'LEFT'
            );
        }
    }, [activeBubbleStyle, bubbleTailDir]);

    // baseImageUrl 동기화 — 항상 현재 (renderKind, placementType) 의 그룹 default 를 따른다.
    // - 신규 모드: categoryId 또는 ARTIFACT 의 placementType 변경 시 새 그룹 default 적용.
    // - 편집 모드: detail 로드로 setCategoryId 호출되면 자동으로 그 카테고리의 renderKind default 적용.
    // - BACKGROUND/TEXT_BUBBLE 등은 default 가 null → 빈 문자열로 클리어.
    //
    // slotGroupMap 변화(리스트 refetch)에도 trigger 되어 사용자 수동 입력이 reset 될 수 있으나,
    // 실제 운영에서 baseImageUrl 수동 입력 케이스는 거의 없어 trade-off 수용.
    useEffect(() => {
        if (!renderKind) {
            setBaseImageUrl('');
            return;
        }
        const key =
            renderKind === 'ARTIFACT'
                ? `ARTIFACT:${placementType}`
                : renderKind;
        const nextGroup = slotGroupMap[key];
        setBaseImageUrl(nextGroup?.defaultBaseImageUrl ?? '');
    }, [renderKind, placementType, slotGroupMap]);

    const codeValid = isEditMode || CODE_REGEX.test(code);
    const bubbleColorValid = HEX_COLOR_REGEX.test(bubbleColorHex);
    const painterFieldsFilled =
        !!bubbleStyleCode &&
        bubbleColorValid &&
        !!bubbleTailDir &&
        (!activeBubbleStyle ||
            activeBubbleStyle.supportedTailDirs.includes(bubbleTailDir));
    const metaFilled =
        codeValid &&
        categoryId !== null &&
        !!nameKo.trim() &&
        !!nameEn.trim() &&
        !!nameJa.trim() &&
        !!nameVi.trim() &&
        priceXp !== '' &&
        sortOrder !== '' &&
        // ARTIFACT renderKind 는 sizePreset/placementType 까지 필수
        (renderKind !== 'ARTIFACT' || (!!sizePreset && !!placementType)) &&
        // TEXT_BUBBLE renderKind 는 V200 painter 필드 3개 + 텍스트 메타 7필드 전부 필수
        (renderKind !== 'TEXT_BUBBLE' ||
            (painterFieldsFilled && isBubbleMetaComplete(bubbleMeta)));

    // ── ARTIFACT 공개 가드 ─────────────────────────────────────────────────
    // isRoomEditorVisible 토글 활성 조건 — 백엔드 V187 가드와 동일.
    // renderKind == ARTIFACT AND image_url IS NOT NULL AND tileW/tileH/placement/sizePreset 모두 채워짐.
    const roomEditorPublishReady =
        isEditMode &&
        renderKind === 'ARTIFACT' &&
        !!detail?.imageUrl &&
        !!sizePreset &&
        !!placementType;
    const roomEditorToggleDisabled =
        renderKind !== 'ARTIFACT' || (!isRoomEditorVisible && !roomEditorPublishReady);

    const isOpenaiBatchBaseConflict =
        mode === 'BATCH' &&
        provider === 'OPENAI' &&
        !!baseImageUrl.trim();

    // ── V190: 시스템 기본 토글 ──────────────────────────────────────────────
    // 노출 조건: 편집 모드 + CHARACTER/BACKGROUND renderKind.
    // disabled 조건: priceXp !== 0 || !isActive.
    // 토글 → 즉시 PATCH (다른 필드 변경과 분리). 실패 시 토글 원복 + 에러 메시지.
    const isDefaultSupportedSlot =
        renderKind === 'CHARACTER' || renderKind === 'BACKGROUND';
    const isDefaultDisabledReason: string | null =
        priceXp !== 0
            ? '가격 0 + 활성 상태에서만 설정 가능합니다'
            : !isActive
            ? '가격 0 + 활성 상태에서만 설정 가능합니다'
            : null;

    const handleToggleDefault = async (next: boolean) => {
        if (!isEditMode || !detail) return;
        setDefaultToggleError(null);
        const previous = isDefault;
        setIsDefault(next); // optimistic
        try {
            await patchMut.mutateAsync({
                code: detail.code,
                body: {isDefault: next},
            });
        } catch (e) {
            // 토글 원복
            setIsDefault(previous);
            const apiErr = e as {errorCode?: number; message?: string};
            const code = apiErr?.errorCode;
            if (code === 5022) {
                setDefaultToggleError(
                    'ARTIFACT 슬롯은 기본 코스튬으로 설정할 수 없습니다.'
                );
            } else if (code === 5023) {
                setDefaultToggleError('가격을 0으로 먼저 변경해야 합니다.');
            } else if (code === 5024) {
                setDefaultToggleError('코스튬을 활성화한 뒤 설정할 수 있습니다.');
            } else {
                setDefaultToggleError(
                    e instanceof Error ? e.message : '기본 코스튬 설정 실패'
                );
            }
        }
    };

    const handleMetaSave = async () => {
        setMetaError(null);
        setMetaSaved(false);
        if (!isEditMode || !detail) return;
        if (priceXp === '' || sortOrder === '') {
            setMetaError('가격과 정렬 순서는 필수입니다.');
            return;
        }
        const body: PatchCostumeRequest = {};
        if (nameKo !== detail.nameKo) body.nameKo = nameKo;
        if (nameEn !== detail.nameEn) body.nameEn = nameEn;
        if (nameJa !== detail.nameJa) body.nameJa = nameJa;
        if (nameVi !== detail.nameVi) body.nameVi = nameVi;
        if ((defaultPrompt ?? '') !== (detail.defaultPrompt ?? '')) {
            body.defaultPrompt = defaultPrompt;
        }
        if (priceXp !== detail.priceXp) body.priceXp = Number(priceXp);
        if (sortOrder !== detail.sortOrder) body.sortOrder = Number(sortOrder);
        if (isActive !== detail.isActive) body.isActive = isActive;
        if (categoryId !== null && categoryId !== detail.categoryId) {
            body.categoryId = categoryId;
        }

        // BACKGROUND renderKind 일 때만 bg 필드 변경 추적. 다른 renderKind 는 의미 없음.
        if (renderKind === 'BACKGROUND') {
            if (bgKind !== (detail.bgKind ?? 'IMAGE')) body.bgKind = bgKind;
            const detailColor = detail.bgColor ?? '';
            if (bgColor !== detailColor) body.bgColor = bgColor;
            const detailTile = detail.bgTileSizePx ?? '';
            if (bgTileSizePx !== detailTile) {
                body.bgTileSizePx = bgTileSizePx === '' ? 0 : Number(bgTileSizePx);
            }
        }

        // ARTIFACT renderKind 일 때만 스티커북 필드 변경 추적.
        if (renderKind === 'ARTIFACT') {
            if (sizePreset !== (detail.sizePreset ?? null)) {
                body.sizePreset = sizePreset;
                // preset 기준 tileW/tileH 자동 동기화
                const map = SIZE_PRESET_TILE_MAP[sizePreset];
                if (map.tileW !== (detail.tileW ?? null)) body.tileW = map.tileW;
                if (map.tileH !== (detail.tileH ?? null)) body.tileH = map.tileH;
            }
            if (placementType !== (detail.placementType ?? null)) {
                body.placementType = placementType;
            }
            if (isRoomEditorVisible !== detail.isRoomEditorVisible) {
                body.isRoomEditorVisible = isRoomEditorVisible;
            }
        }

        // TEXT_BUBBLE renderKind 일 때만 텍스트 메타 + V200 painter 필드 변경 추적.
        // patch 는 null=변경없음 — 채워진 필드 중 detail 과 달라진 것만 전송(빈값은 미전송).
        if (renderKind === 'TEXT_BUBBLE') {
            for (const key of ALL_BUBBLE_META_KEYS) {
                const cur = bubbleMeta[key];
                if (cur === '') continue; // 빈값은 부분수정에서 변경 안 함
                const prev = detail[key] ?? null;
                if (Number(cur) !== prev) {
                    body[key] = Number(cur);
                }
            }
            // V200 painter 3필드. 빈값이면 변경 없음 / detail 과 달라진 경우만 전송.
            if (bubbleStyleCode && bubbleStyleCode !== (detail.bubbleStyleCode ?? '')) {
                body.bubbleStyleCode = bubbleStyleCode;
            }
            if (bubbleColorHex && bubbleColorHex !== (detail.bubbleColorHex ?? '')) {
                body.bubbleColorHex = bubbleColorHex;
            }
            if (bubbleTailDir && bubbleTailDir !== (detail.bubbleTailDir ?? '')) {
                body.bubbleTailDir = bubbleTailDir;
            }
        }

        if (Object.keys(body).length === 0) {
            setMetaSaved(true);
            return;
        }
        try {
            await patchMut.mutateAsync({code: detail.code, body});
            setMetaSaved(true);
        } catch (e) {
            // 5013: ARTIFACT_NOT_READY_FOR_PUBLISH — 공개 가드 실패
            const apiErr = e as {errorCode?: number; message?: string};
            if (apiErr?.errorCode === 5013) {
                setMetaError(
                    '이미지 finalize 및 메타 필수 항목(크기 프리셋·배치 타입·tileW·tileH)을 완료한 뒤 공개 토글을 다시 시도해주세요.'
                );
                // 토글 원상복귀
                if (detail) setIsRoomEditorVisible(detail.isRoomEditorVisible ?? false);
            } else {
                setMetaError(e instanceof Error ? e.message : '메타 저장 실패');
            }
        }
    };

    const handleGenerate = async () => {
        setGenerateError(null);
        if (!metaFilled) {
            setGenerateError('메타 정보(code, 이름 4개, 가격, 정렬)를 먼저 채워주세요.');
            return;
        }
        if (!defaultPrompt.trim()) {
            setGenerateError('기본 userContext는 필수입니다.');
            return;
        }
        if (isOpenaiBatchBaseConflict) {
            setGenerateError(
                'OpenAI Batch는 base image와 함께 사용할 수 없습니다. SYNC 모드 또는 Gemini를 사용하세요.'
            );
            return;
        }
        // ARTIFACT renderKind 는 sizePreset/placementType 필수 (백엔드와 동일 invariant)
        if (renderKind === 'ARTIFACT' && (!sizePreset || !placementType)) {
            setGenerateError('ARTIFACT 카테고리는 크기 프리셋과 배치 타입이 필수입니다.');
            return;
        }
        // V200: TEXT_BUBBLE 은 painter 단일 경로 — AI generate 진입 자체가 백엔드 5035 거부.
        // 폼에서도 사전 차단.
        if (renderKind === 'TEXT_BUBBLE') {
            setGenerateError(
                'TEXT_BUBBLE(painter) 카테고리는 AI 생성을 지원하지 않습니다. 메타 저장만 사용하세요.'
            );
            return;
        }
        if (categoryId == null) {
            setGenerateError('카테고리를 먼저 선택하세요.');
            return;
        }
        try {
            await generateMut.mutateAsync({
                code,
                categoryId,
                nameKo,
                nameEn,
                nameJa,
                nameVi,
                defaultPrompt: defaultPrompt.trim(),
                priceXp: Number(priceXp),
                provider,
                mode,
                imageCount,
                baseImageUrl: baseImageUrl.trim() || undefined,
                sizePreset: renderKind === 'ARTIFACT' ? sizePreset : undefined,
                placementType: renderKind === 'ARTIFACT' ? placementType : undefined,
            });
            // history invalidate 는 generate mutation onSuccess 에서 처리.
            // 새 request 카드가 이력 최상단에 자동 노출되고, 진행 중 상태면 5초 polling 시작.
            setSelectedRequestId(null);
            setSelectedResultId(null);
            setFinalizeSuccess(false);
        } catch (e) {
            setGenerateError(e instanceof Error ? e.message : 'AI 생성 요청 실패');
        }
    };

    const handleDirectUpload = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        // input 재선택을 위해 즉시 value 초기화
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (!file) return;
        if (!isEditMode || !detail) return;

        setUploadError(null);
        setUploadSuccess(false);

        if (!file.type.startsWith('image/')) {
            setUploadError('이미지 파일만 업로드 가능합니다.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setUploadError('파일 크기는 5MB 이하여야 합니다.');
            return;
        }

        try {
            setUploading(true);
            const res = await uploadApi.uploadImage(file, 'eppi-costumes');
            await patchMut.mutateAsync({
                code: detail.code,
                body: {imageUrl: res.fileUrl},
            });
            setUploadSuccess(true);
        } catch (e) {
            setUploadError(e instanceof Error ? e.message : '업로드 실패');
        } finally {
            setUploading(false);
        }
    };

    const handleFinalize = async () => {
        setFinalizeError(null);
        setFinalizeSuccess(false);
        if (!selectedResultId) {
            setFinalizeError('이미지를 1장 선택하세요.');
            return;
        }
        if (priceXp === '' || sortOrder === '') {
            setFinalizeError('가격과 정렬은 필수입니다.');
            return;
        }
        // ARTIFACT renderKind 는 sizePreset/placementType 필수 (백엔드와 동일 invariant)
        if (renderKind === 'ARTIFACT' && (!sizePreset || !placementType)) {
            setFinalizeError('ARTIFACT 카테고리는 크기 프리셋과 배치 타입이 필수입니다.');
            return;
        }
        // V200: TEXT_BUBBLE 은 painter 단일 경로 — finalize 진입 자체가 백엔드 5035 거부.
        if (renderKind === 'TEXT_BUBBLE') {
            setFinalizeError(
                'TEXT_BUBBLE(painter) 카테고리는 AI finalize 를 지원하지 않습니다.'
            );
            return;
        }
        if (categoryId == null) {
            setFinalizeError('카테고리를 먼저 선택하세요.');
            return;
        }
        try {
            const presetTile = renderKind === 'ARTIFACT'
                ? SIZE_PRESET_TILE_MAP[sizePreset]
                : null;
            await finalizeMut.mutateAsync({
                code,
                body: {
                    aiImageResultId: selectedResultId,
                    categoryId,
                    nameKo,
                    nameEn,
                    nameJa,
                    nameVi,
                    defaultPrompt: defaultPrompt.trim(),
                    priceXp: Number(priceXp),
                    sortOrder: Number(sortOrder),
                    isActive,
                    sizePreset: renderKind === 'ARTIFACT' ? sizePreset : undefined,
                    placementType: renderKind === 'ARTIFACT' ? placementType : undefined,
                    tileW: presetTile?.tileW,
                    tileH: presetTile?.tileH,
                },
            });
            setFinalizeSuccess(true);
            setSelectedResultId(null);
            setSelectedRequestId(null);
            if (!isEditMode) {
                // 신규 → 편집 라우트로 전환 (URL 갱신, replace 로 뒤로가기 시 /new 안 나오게)
                navigate(`/eppi-costumes/${code}/edit`, {replace: true});
            }
        } catch (e) {
            setFinalizeError(e instanceof Error ? e.message : '확정 실패');
        }
    };

    // 편집 모드 detail 로드 전이면 로딩 표시
    if (isEditMode && detailQuery.isLoading) {
        return (
            <Box sx={{p: 3, display: 'flex', justifyContent: 'center'}}>
                <CircularProgress />
            </Box>
        );
    }
    if (isEditMode && detailQuery.error) {
        return (
            <Box sx={{p: 3}}>
                <Alert severity="error">
                    {detailQuery.error.message ?? '코스튬 정보를 불러오지 못했습니다.'}
                </Alert>
                <Button
                    sx={{mt: 2}}
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/eppi-costumes')}
                >
                    목록으로
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{p: 3, maxWidth: 1100, mx: 'auto'}}>
            {/* 헤더: breadcrumb + 목록 버튼 */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{mb: 2}}
            >
                <Breadcrumbs>
                    <MuiLink
                        component="button"
                        onClick={() => navigate('/eppi-costumes')}
                        underline="hover"
                        color="inherit"
                    >
                        코스튬
                    </MuiLink>
                    <Typography color="text.primary">
                        {isEditMode ? `${codeParam} 편집` : '새 코스튬'}
                    </Typography>
                </Breadcrumbs>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/eppi-costumes')}
                    size="small"
                >
                    목록
                </Button>
            </Stack>

            {finalizeSuccess && (
                <Alert severity="success" sx={{mb: 2}} onClose={() => setFinalizeSuccess(false)}>
                    이미지 적용이 완료되었습니다.
                </Alert>
            )}

            {/* 섹션 1. 메타 정보 */}
            <Section
                title="1. 메타 정보"
                subtitle={
                    isEditMode
                        ? '이름·가격·정렬·활성 여부 편집 (code 변경 불가)'
                        : '신규 등록 시 code 입력. 이름/가격/정렬을 채운 후 AI 생성으로 진행'
                }
            >
                <Stack spacing={2}>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <TextField
                            label="code (영문 대문자)"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            fullWidth
                            disabled={isEditMode}
                            error={!!code && !codeValid}
                            helperText={
                                isEditMode
                                    ? '편집 모드: code 변경 불가'
                                    : 'A-Z, 0-9, _ 사용. 예: BACHELOR, STARRY_NIGHT'
                            }
                        />
                        <TextField
                            select
                            label="카테고리"
                            value={categoryId ?? ''}
                            onChange={(e) =>
                                setCategoryId(
                                    e.target.value === ''
                                        ? null
                                        : Number(e.target.value)
                                )
                            }
                            sx={{minWidth: 260}}
                            disabled={categoriesQuery.isLoading}
                            helperText={
                                categoriesQuery.isLoading
                                    ? '카테고리 로드 중...'
                                    : isEditMode && !!detail?.imageUrl
                                    ? 'finalized 코스튬은 동일 렌더링 종류(renderKind) 카테고리로만 이동할 수 있습니다.'
                                    : isEditMode
                                    ? 'draft 코스튬은 다른 렌더링 종류의 카테고리로도 이동할 수 있습니다.'
                                    : '신규 등록 — 카테고리 선택 시 renderKind 가 자동 결정됩니다.'
                            }
                        >
                            {categories.length === 0 && (
                                <MenuItem value="" disabled>
                                    (카테고리 없음)
                                </MenuItem>
                            )}
                            {categories.map((cat) => {
                                // finalized 편집 모드: 다른 renderKind 카테고리는 선택 불가.
                                const finalizedLocked =
                                    isEditMode &&
                                    !!detail?.imageUrl &&
                                    !!detail.renderKind &&
                                    cat.renderKind !== detail.renderKind;
                                return (
                                    <MenuItem
                                        key={cat.id}
                                        value={cat.id}
                                        disabled={finalizedLocked}
                                    >
                                        {cat.displayNameKo} (
                                        {RENDER_KIND_LABEL_KO[cat.renderKind]})
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    </Stack>

                    {/* V190: 시스템 기본 코스튬 토글 — 편집 모드 + CHARACTER/BACKGROUND 슬롯만 */}
                    {isEditMode && isDefaultSupportedSlot && (
                        <Stack spacing={1}>
                            <Tooltip
                                title={isDefaultDisabledReason ?? ''}
                                placement="top-start"
                            >
                                <span>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isDefault}
                                                onChange={(_, v) =>
                                                    handleToggleDefault(v)
                                                }
                                                size="small"
                                                disabled={
                                                    !!isDefaultDisabledReason ||
                                                    patchMut.isPending
                                                }
                                            />
                                        }
                                        label={
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <Typography variant="body2">
                                                    시스템 기본으로 설정
                                                </Typography>
                                                {isDefault && (
                                                    <Chip
                                                        size="small"
                                                        label="기본"
                                                        color="success"
                                                    />
                                                )}
                                            </Stack>
                                        }
                                    />
                                </span>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary">
                                ON 시 미보유 사용자에게 이 코스튬이 기본으로 노출됩니다.
                                같은 슬롯의 다른 기본 코스튬은 자동 해제됩니다.
                            </Typography>
                            {defaultToggleError && (
                                <Alert
                                    severity="error"
                                    onClose={() => setDefaultToggleError(null)}
                                >
                                    {defaultToggleError}
                                </Alert>
                            )}
                        </Stack>
                    )}

                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <TextField
                            label="한국어 이름"
                            value={nameKo}
                            onChange={(e) => setNameKo(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="영어 이름"
                            value={nameEn}
                            onChange={(e) => setNameEn(e.target.value)}
                            fullWidth
                        />
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <TextField
                            label="일본어 이름"
                            value={nameJa}
                            onChange={(e) => setNameJa(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="베트남어 이름"
                            value={nameVi}
                            onChange={(e) => setNameVi(e.target.value)}
                            fullWidth
                        />
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="center">
                        <TextField
                            label="가격 (XP)"
                            type="number"
                            value={priceXp}
                            onChange={(e) =>
                                setPriceXp(e.target.value === '' ? '' : Number(e.target.value))
                            }
                            fullWidth
                        />
                        <TextField
                            label="정렬 순서"
                            type="number"
                            value={sortOrder}
                            onChange={(e) =>
                                setSortOrder(e.target.value === '' ? '' : Number(e.target.value))
                            }
                            fullWidth
                        />
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{minWidth: 120}}
                        >
                            <Typography variant="body2">활성</Typography>
                            <Switch
                                checked={isActive}
                                onChange={(_, v) => setIsActive(v)}
                                size="small"
                            />
                        </Stack>
                    </Stack>

                    {/* BACKGROUND renderKind 전용 — 표현 모드 (SOLID/TILE/IMAGE). */}
                    {renderKind === 'BACKGROUND' && (
                        <Stack spacing={1.5} sx={{
                            p: 2,
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'divider',
                            bgcolor: 'background.default',
                        }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                배경 표현 모드
                            </Typography>
                            <RadioGroup
                                row
                                value={bgKind}
                                onChange={(_, v) => setBgKind(v as BackgroundKind)}
                            >
                                {BACKGROUND_KINDS.map((k) => (
                                    <FormControlLabel
                                        key={k}
                                        value={k}
                                        control={<Radio size="small" />}
                                        label={`${BACKGROUND_KIND_LABEL_KO[k]} (${k})`}
                                    />
                                ))}
                            </RadioGroup>
                            {(bgKind === 'SOLID' || bgKind === 'TILE') && (
                                <Stack
                                    direction={{xs: 'column', sm: 'row'}}
                                    spacing={2}
                                    alignItems="center"
                                >
                                    <TextField
                                        label="배경색 (#RRGGBB)"
                                        value={bgColor}
                                        onChange={(e) => setBgColor(e.target.value)}
                                        placeholder="#FFFDF8"
                                        size="small"
                                        sx={{flex: 1}}
                                    />
                                    {/* 컬러피커 — native input, 작은 사이즈 */}
                                    <input
                                        type="color"
                                        value={
                                            /^#[0-9a-fA-F]{6}$/.test(bgColor)
                                                ? bgColor
                                                : '#ffffff'
                                        }
                                        onChange={(e) => setBgColor(e.target.value)}
                                        style={{
                                            width: 40,
                                            height: 40,
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                        }}
                                    />
                                    {bgKind === 'TILE' && (
                                        <TextField
                                            label="타일 크기 (px)"
                                            type="number"
                                            value={bgTileSizePx}
                                            onChange={(e) =>
                                                setBgTileSizePx(
                                                    e.target.value === ''
                                                        ? ''
                                                        : Number(e.target.value)
                                                )
                                            }
                                            size="small"
                                            sx={{width: 160}}
                                            helperText="32~128 권장"
                                        />
                                    )}
                                </Stack>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                {bgKind === 'SOLID' &&
                                    '단색 — 이미지 자산 없이 hex 컬러만 사용. AI/직접 업로드 섹션은 무시됩니다.'}
                                {bgKind === 'TILE' &&
                                    '타일 패턴 — 작은 타일 이미지 1장 + 배경색. 모바일에서 자동 반복 렌더.'}
                                {bgKind === 'IMAGE' &&
                                    '풀 일러스트 — 캔버스 전체 이미지 (기존 동작).'}
                            </Typography>
                        </Stack>
                    )}

                    {/* ARTIFACT renderKind 전용 — 크기 프리셋 + 배치 타입 + 공개 토글. */}
                    {renderKind === 'ARTIFACT' && (
                        <Stack
                            spacing={1.5}
                            sx={{
                                p: 2,
                                borderRadius: 1,
                                border: '1px dashed',
                                borderColor: 'divider',
                                bgcolor: 'background.default',
                            }}
                        >
                            <Typography variant="subtitle2" color="text.secondary">
                                스티커북 설정 (ARTIFACT 카테고리 전용)
                            </Typography>

                            <Stack
                                direction={{xs: 'column', sm: 'row'}}
                                spacing={2}
                            >
                                <TextField
                                    select
                                    label="크기"
                                    value={sizePreset}
                                    onChange={(e) => {
                                        const next = e.target.value as CostumeSizePreset;
                                        setSizePreset(next);
                                        if (
                                            isEditMode &&
                                            detail?.sizePreset &&
                                            detail.sizePreset !== next
                                        ) {
                                            setPresetWarning(
                                                '크기가 바뀌면 캔버스 점유 비율이 달라집니다. 자연 비율은 이미지 그대로 유지됩니다.'
                                            );
                                        } else {
                                            setPresetWarning(null);
                                        }
                                    }}
                                    fullWidth
                                    helperText={(() => {
                                        const pct = sizePreset === 'SMALL' ? 10 : sizePreset === 'MEDIUM' ? 18 : 28;
                                        const aspect = detail?.imageAspectRatio;
                                        return aspect
                                            ? `이미지 자연 비율 ${aspect.toFixed(2)} (W/H) · 본체 긴 변이 캔버스 짧은쪽의 ${pct}%`
                                            : `이미지 자연 비율은 업로드 후 자동 측정 · 본체 긴 변이 캔버스 짧은쪽의 ${pct}%`;
                                    })()}
                                >
                                    {COSTUME_SIZE_PRESETS.map((p) => (
                                        <MenuItem key={p} value={p}>
                                            {SIZE_PRESET_LABEL_KO[p]}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                <Box sx={{flex: 1}}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        배치 타입
                                    </Typography>
                                    <RadioGroup
                                        row
                                        value={placementType}
                                        onChange={(_, v) => {
                                            const next = v as PlacementType;
                                            setPlacementType(next);
                                            if (
                                                isEditMode &&
                                                detail?.placementType &&
                                                detail.placementType !== next
                                            ) {
                                                setPresetWarning(
                                                    '배치 타입을 변경하면 AI 그룹/systemContext 도 달라집니다. 기존 이미지가 새 배치에 안 맞으면 재생성 권장.'
                                                );
                                            }
                                        }}
                                    >
                                        {PLACEMENT_TYPES.map((p) => (
                                            <FormControlLabel
                                                key={p}
                                                value={p}
                                                control={<Radio size="small" />}
                                                label={PLACEMENT_TYPE_LABEL_KO[p]}
                                            />
                                        ))}
                                    </RadioGroup>
                                </Box>
                            </Stack>

                            {/* preset/placement 변경 warning */}
                            {presetWarning && (
                                <Alert
                                    severity="warning"
                                    onClose={() => setPresetWarning(null)}
                                >
                                    {presetWarning}
                                </Alert>
                            )}

                            {/* 공개 토글 — 가드 미충족 시 disabled + Tooltip */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{pt: 1}}
                            >
                                <Tooltip
                                    title={
                                        roomEditorToggleDisabled && !isRoomEditorVisible
                                            ? '이미지 finalize + 메타 필수 항목(크기 프리셋·배치 타입) 완료 후 공개 가능'
                                            : ''
                                    }
                                >
                                    <span>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isRoomEditorVisible}
                                                    onChange={(_, v) =>
                                                        setIsRoomEditorVisible(v)
                                                    }
                                                    size="small"
                                                    disabled={roomEditorToggleDisabled}
                                                />
                                            }
                                            label="방 편집기 공개 (isRoomEditorVisible)"
                                        />
                                    </span>
                                </Tooltip>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    ON 시 사용자 코스튬샵 스티커북 카테고리에 노출. 백엔드 가드 미충족이면 메타 저장 시
                                    400 (5013) 으로 거부됩니다.
                                </Typography>
                            </Stack>

                            {/* AI 그룹 안내 — placementType 별 자동 라우팅 */}
                            <Alert severity="info" icon={<InfoOutlined fontSize="small" />}>
                                AI 생성 시 백엔드가 `placementType` 에 따라 자동으로
                                `Eppi Costume — Artifact ({placementType})` 그룹으로 라우팅합니다.
                                systemContext preview는 아래 섹션에서 placementType 변경에 따라 갱신됩니다.
                            </Alert>
                        </Stack>
                    )}

                    {/* TEXT_BUBBLE renderKind 전용 — V200 painter 카탈로그 + 텍스트 표현 메타. */}
                    {renderKind === 'TEXT_BUBBLE' && (
                        <Stack
                            spacing={2}
                            sx={{
                                p: 2,
                                borderRadius: 1,
                                border: '1px dashed',
                                borderColor: 'divider',
                                bgcolor: 'background.default',
                            }}
                        >
                            <Typography variant="subtitle2" color="text.secondary">
                                텍스트 말풍선 설정 (TEXT_BUBBLE / painter)
                            </Typography>
                            <Alert severity="info" icon={<InfoOutlined fontSize="small" />}>
                                V200 painter — 자산 이미지 없이 shape 카탈로그 + 색상 + 꼬리 방향으로
                                직접 그립니다. 이미지 업로드 / AI 생성 / 9-patch slice 좌표는 사용하지 않습니다.
                            </Alert>

                            {/* Painter 카탈로그 (style code) */}
                            <Stack spacing={1}>
                                <Typography variant="caption" fontWeight={600}>
                                    bubble_style_code (painter 카탈로그)
                                </Typography>
                                <TextField
                                    select
                                    label="스타일"
                                    size="small"
                                    value={bubbleStyleCode}
                                    onChange={(e) =>
                                        setBubbleStyleCode(e.target.value)
                                    }
                                    fullWidth
                                    helperText={
                                        bubbleStylesQuery.isLoading
                                            ? '카탈로그 로드 중...'
                                            : `shape=${activeBubbleStyle?.shape ?? '-'} · outline=${activeBubbleStyle?.outlineWidth ?? '-'} · tail=${activeBubbleStyle?.tailLengthDp ?? '-'}dp×${activeBubbleStyle?.tailWidthDp ?? '-'}dp`
                                    }
                                >
                                    {bubbleStyles.map((s) => (
                                        <MenuItem key={s.code} value={s.code}>
                                            {s.code}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>

                            {/* 본체 색상 #RRGGBB */}
                            <Stack spacing={1}>
                                <Typography variant="caption" fontWeight={600}>
                                    bubble_color_hex (#RRGGBB)
                                </Typography>
                                <Stack
                                    direction={{xs: 'column', sm: 'row'}}
                                    spacing={2}
                                    alignItems="center"
                                >
                                    <TextField
                                        label="본체 fill 색상"
                                        value={bubbleColorHex}
                                        onChange={(e) =>
                                            setBubbleColorHex(e.target.value)
                                        }
                                        placeholder="#A8D8F0"
                                        size="small"
                                        error={
                                            !!bubbleColorHex && !bubbleColorValid
                                        }
                                        helperText={
                                            !!bubbleColorHex && !bubbleColorValid
                                                ? '#RRGGBB 형식이어야 합니다 (예: #A8D8F0)'
                                                : '본체 fill 색상. 글자색은 코코아 브라운으로 고정입니다.'
                                        }
                                        sx={{flex: 1}}
                                    />
                                    <input
                                        type="color"
                                        value={
                                            bubbleColorValid
                                                ? bubbleColorHex
                                                : '#ffffff'
                                        }
                                        onChange={(e) =>
                                            setBubbleColorHex(
                                                e.target.value.toUpperCase()
                                            )
                                        }
                                        style={{
                                            width: 40,
                                            height: 40,
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Stack>
                            </Stack>

                            {/* 꼬리 방향 LEFT/RIGHT/NONE — style.supportedTailDirs 만 활성화 */}
                            <Stack spacing={1}>
                                <Typography variant="caption" fontWeight={600}>
                                    bubble_tail_dir (꼬리 방향)
                                </Typography>
                                <RadioGroup
                                    row
                                    value={bubbleTailDir}
                                    onChange={(_, v) =>
                                        setBubbleTailDir(v as BubbleTailDir)
                                    }
                                >
                                    {(
                                        ['LEFT', 'RIGHT', 'NONE'] as BubbleTailDir[]
                                    ).map((dir) => {
                                        const supported =
                                            activeBubbleStyle?.supportedTailDirs.includes(
                                                dir
                                            ) ?? true;
                                        return (
                                            <FormControlLabel
                                                key={dir}
                                                value={dir}
                                                control={
                                                    <Radio
                                                        size="small"
                                                        disabled={!supported}
                                                    />
                                                }
                                                label={`${BUBBLE_TAIL_DIR_LABEL_KO[dir]} (${dir})`}
                                                disabled={!supported}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                                <Typography variant="caption" color="text.secondary">
                                    카탈로그의 supportedTailDirs 에 포함된 값만 활성화됩니다.
                                    NONE 은 THOUGHT_PUFF 전용.
                                </Typography>
                            </Stack>

                            {/* contentPadding — dp */}
                            <Stack spacing={1}>
                                <Typography variant="caption" fontWeight={600}>
                                    텍스트 안쪽 여백 contentPadding (dp)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    말풍선 프레임과 텍스트 사이의 여백입니다. 단위는 dp(화면 밀도 독립).
                                </Typography>
                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                    {BUBBLE_PAD_FIELDS.map((f) => (
                                        <TextField
                                            key={f.key}
                                            label={f.label}
                                            type="number"
                                            size="small"
                                            value={bubbleMeta[f.key]}
                                            onChange={(e) =>
                                                setBubbleField(f.key, e.target.value)
                                            }
                                            fullWidth
                                        />
                                    ))}
                                </Stack>
                            </Stack>

                            {/* 텍스트 표현 — sp / dp / chars */}
                            <Stack spacing={1}>
                                <Typography variant="caption" fontWeight={600}>
                                    텍스트 표현
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    글자 크기는 sp, 최대 너비는 dp(초과 시 자동 줄바꿈), 최대 글자 수는 입력
                                    상한입니다. 글자색은 코코아 브라운으로 고정되어 입력하지 않습니다.
                                </Typography>
                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                    {BUBBLE_TEXT_FIELDS.map((f) => (
                                        <TextField
                                            key={f.key}
                                            label={f.label}
                                            type="number"
                                            size="small"
                                            value={bubbleMeta[f.key]}
                                            onChange={(e) =>
                                                setBubbleField(f.key, e.target.value)
                                            }
                                            fullWidth
                                        />
                                    ))}
                                </Stack>
                            </Stack>

                            {/* 선택 메타 (V196/V197) — 캐릭터 박힌 말풍선 디자인 전용 박스 압축 방지 */}
                            <Stack spacing={1}>
                                <Typography variant="caption" fontWeight={600}>
                                    선택 메타 — 박스 압축 방지 (캐릭터 박힌 디자인용)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    캐릭터가 박힌 말풍선처럼 본체 비율을 보호해야 하는 디자인에서만 입력합니다.
                                    단순 사각/꼬리 디자인은 비워두면 자유 가변(0/null=강제 없음).
                                </Typography>
                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                    {BUBBLE_OPTIONAL_FIELDS.map((f) => (
                                        <TextField
                                            key={f.key}
                                            label={f.label}
                                            type="number"
                                            size="small"
                                            value={bubbleMeta[f.key]}
                                            onChange={(e) =>
                                                setBubbleField(f.key, e.target.value)
                                            }
                                            helperText={f.helper}
                                            fullWidth
                                        />
                                    ))}
                                </Stack>
                            </Stack>

                            {/* 미입력 경고 — V200 painter: painter 3필드 + 텍스트 메타 7필드 필수 */}
                            {(!painterFieldsFilled ||
                                !isBubbleMetaComplete(bubbleMeta)) && (
                                <Alert
                                    severity="warning"
                                    icon={<InfoOutlined fontSize="small" />}
                                >
                                    {isEditMode
                                        ? '비어 있는 필드는 메타 저장 시 변경되지 않습니다(부분 수정).'
                                        : 'TEXT_BUBBLE(painter) 등록에는 painter 3필드 (style / color / tail) + 텍스트 메타 7필드 (pad×4 + 글자 크기 + 최대 너비 + 최대 글자 수) 가 모두 필요합니다.'}
                                </Alert>
                            )}
                        </Stack>
                    )}

                    {metaError && <Alert severity="error">{metaError}</Alert>}
                    {metaSaved && (
                        <Alert severity="success" onClose={() => setMetaSaved(false)}>
                            메타 정보가 저장되었습니다.
                        </Alert>
                    )}

                    {isEditMode && (
                        <Stack direction="row" justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                startIcon={<Save />}
                                onClick={handleMetaSave}
                                disabled={patchMut.isPending}
                            >
                                {patchMut.isPending ? '저장 중...' : '메타 저장'}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </Section>

            {/* 섹션 2. 현재 이미지 (편집 모드만 / TEXT_BUBBLE renderKind 제외 — painter 는 자산 없음) */}
            {isEditMode && detail && renderKind !== 'TEXT_BUBBLE' && (
                <Section
                    title="2. 현재 이미지"
                    subtitle="직접 업로드하거나 아래 AI 생성으로 교체할 수 있습니다."
                >
                    <Stack spacing={2}>
                        {detail.imageUrl ? (
                            <Stack alignItems="center" spacing={1}>
                                <Box
                                    component="img"
                                    src={detail.imageUrl}
                                    alt={detail.nameKo}
                                    sx={{
                                        width: 250,
                                        height: 250,
                                        objectFit: 'contain',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        bgcolor: 'grey.50',
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    <MuiLink href={detail.imageUrl} target="_blank" rel="noreferrer">
                                        원본 보기
                                    </MuiLink>
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack alignItems="center" spacing={1} sx={{py: 4}}>
                                <ImageIcon sx={{fontSize: 48, color: 'text.disabled'}} />
                                <Typography variant="body2" color="text.secondary">
                                    아직 이미지가 없습니다. 직접 업로드하거나 아래 섹션에서 AI로 생성하세요.
                                </Typography>
                            </Stack>
                        )}

                        {/* 직접 업로드 */}
                        <Box
                            sx={{
                                border: '1px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                bgcolor: 'action.hover',
                            }}
                        >
                            <Stack spacing={1.5}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                >
                                    <Stack spacing={0.5} sx={{flexGrow: 1, minWidth: 0}}>
                                        <Typography variant="body2" fontWeight={600}>
                                            직접 이미지 등록
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            로컬 파일을 R2에 업로드해 코스튬 이미지로 즉시 교체합니다.
                                            (PNG/JPG · 최대 5MB · 1:1 권장)
                                            <br />
                                            업로드 시 `aiImageRequestId`가 NULL로 클리어되어 AI 생성 출처가
                                            아닌 직접 등록으로 추적됩니다.
                                        </Typography>
                                    </Stack>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={
                                            uploading ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <CloudUpload />
                                            )
                                        }
                                        disabled={uploading}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        {uploading ? '업로드 중...' : '파일 선택'}
                                    </Button>
                                </Stack>
                                {uploadError && (
                                    <Alert severity="error" onClose={() => setUploadError(null)}>
                                        {uploadError}
                                    </Alert>
                                )}
                                {uploadSuccess && (
                                    <Alert
                                        severity="success"
                                        onClose={() => setUploadSuccess(false)}
                                    >
                                        이미지가 업데이트되었습니다.
                                    </Alert>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleDirectUpload}
                                    style={{display: 'none'}}
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </Section>
            )}

            {/* 섹션 3. AI 이미지 생성 — TEXT_BUBBLE(painter) renderKind 에서는 진입 자체가 5035 거부되므로 숨김 */}
            {renderKind !== 'TEXT_BUBBLE' && (
            <Section
                title={isEditMode ? '3. AI 이미지 재생성' : '2. AI 이미지 생성'}
                subtitle="테마/옵션 입력 → 생성 요청 → 결과 갤러리에서 선택 → 확정"
            >
                <Stack spacing={2}>
                    {/* 그룹 systemContext readonly preview — V179 시드된 캐릭터 일관성 프롬프트 */}
                    {systemContext && systemContext.trim() && (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                bgcolor: 'action.hover',
                                borderStyle: 'dashed',
                            }}
                        >
                            <Stack spacing={1}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    <InfoOutlined
                                        fontSize="small"
                                        color="info"
                                    />
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{flexGrow: 1}}
                                    >
                                        {renderKind ? RENDER_KIND_LABEL_KO[renderKind] : ''} 그룹 systemContext (자동 적용)
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            setSystemContextOpen((v) => !v)
                                        }
                                        aria-label={
                                            systemContextOpen
                                                ? '접기'
                                                : '펼치기'
                                        }
                                    >
                                        {systemContextOpen ? (
                                            <ExpandLess fontSize="small" />
                                        ) : (
                                            <ExpandMore fontSize="small" />
                                        )}
                                    </IconButton>
                                </Stack>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {activeGroup?.groupName ?? ''} 그룹의 systemContext가 모든 AI 생성에
                                    자동 적용됩니다. 편집은 그룹 관리 (/admin/ai-images)에서 가능합니다.
                                </Typography>
                                <Collapse in={systemContextOpen}>
                                    <Box
                                        component="pre"
                                        sx={{
                                            m: 0,
                                            p: 1.5,
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            bgcolor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            fontFamily:
                                                'ui-monospace, SFMono-Regular, Menlo, monospace',
                                            fontSize: 12,
                                            lineHeight: 1.5,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            color: 'text.primary',
                                        }}
                                    >
                                        {systemContext}
                                    </Box>
                                </Collapse>
                            </Stack>
                        </Paper>
                    )}

                    <TextField
                        label="기본 userContext"
                        value={defaultPrompt}
                        onChange={(e) => setDefaultPrompt(e.target.value)}
                        fullWidth
                        multiline
                        rows={8}
                        placeholder="예: Theme: starry study night ..."
                        helperText="코스튬 canonical 프롬프트. 메타 저장/확정 시 eppi_costumes.default_prompt 로 저장되고, 생성 요청에도 그대로 사용됩니다."
                    />

                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <Box sx={{flex: 1}}>
                            <Typography variant="caption" color="text.secondary">
                                Provider
                            </Typography>
                            <ToggleButtonGroup
                                value={provider}
                                exclusive
                                size="small"
                                fullWidth
                                onChange={(_, v) => v && setProvider(v)}
                            >
                                <ToggleButton value="GEMINI">GEMINI</ToggleButton>
                                <ToggleButton value="OPENAI">OPENAI</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Box sx={{flex: 1}}>
                            <Typography variant="caption" color="text.secondary">
                                Mode
                            </Typography>
                            <ToggleButtonGroup
                                value={mode}
                                exclusive
                                size="small"
                                fullWidth
                                onChange={(_, v) => v && setMode(v)}
                            >
                                <ToggleButton value="SYNC">SYNC</ToggleButton>
                                <ToggleButton value="BATCH">BATCH</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Stack>

                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                                Image Count
                            </Typography>
                            <Chip
                                size="small"
                                label={mode === 'BATCH' ? 1 : imageCount}
                                color="primary"
                                variant="outlined"
                            />
                            {mode === 'BATCH' && (
                                <Typography variant="caption" color="text.secondary">
                                    (BATCH 모드는 1장 고정)
                                </Typography>
                            )}
                        </Stack>
                        <Slider
                            value={imageCount}
                            min={1}
                            max={5}
                            step={1}
                            marks
                            disabled={mode === 'BATCH'}
                            onChange={(_, v) =>
                                setImageCount(Array.isArray(v) ? v[0] : v)
                            }
                            sx={{mt: 0.5}}
                        />
                    </Box>

                    <TextField
                        label="Base Image URL (선택)"
                        value={baseImageUrl}
                        onChange={(e) => setBaseImageUrl(e.target.value)}
                        fullWidth
                        placeholder="base image 미사용 시 비워두세요"
                        helperText={
                            'Gemini는 SYNC/BATCH 모두 base image 사용 가능. OpenAI는 SYNC 만 가능 (BATCH+base 차단).'
                        }
                    />

                    {isOpenaiBatchBaseConflict && (
                        <Alert severity="warning">
                            OpenAI Batch는 base image 미지원입니다. SYNC 모드 또는 Gemini를
                            사용하세요.
                        </Alert>
                    )}

                    {generateError && <Alert severity="error">{generateError}</Alert>}

                    <Stack direction="row" justifyContent="flex-end">
                        <Tooltip
                            title={
                                isOpenaiBatchBaseConflict
                                    ? 'OpenAI Batch는 base image와 함께 사용할 수 없습니다.'
                                    : ''
                            }
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    startIcon={<AutoAwesome />}
                                    onClick={handleGenerate}
                                    disabled={
                                        generateMut.isPending ||
                                        isOpenaiBatchBaseConflict ||
                                        !defaultPrompt.trim() ||
                                        !metaFilled
                                    }
                                >
                                    {generateMut.isPending ? 'AI 요청 중...' : 'AI 생성 요청'}
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Section>
            )}

            {/* 섹션 4. 생성 요청 이력 + 결과 갤러리 — TEXT_BUBBLE(painter) 에서는 history 없음 */}
            {historyCode && renderKind !== 'TEXT_BUBBLE' && (
                <Section
                    title={isEditMode ? '4. 생성 요청 이력' : '3. 생성 요청 이력'}
                    subtitle="이 코스튬의 모든 generate 요청과 후보 결과. 이미지 1장 선택 → [이 결과로 확정]"
                >
                    <Stack spacing={2}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography variant="caption" color="text.secondary">
                                총 {history.length}건
                                {historyQuery.isFetching && ' · 새로고침 중...'}
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<Refresh />}
                                onClick={() => historyQuery.refetch()}
                                disabled={historyQuery.isFetching}
                            >
                                새로고침
                            </Button>
                        </Stack>

                        {historyQuery.isLoading && (
                            <Stack alignItems="center" sx={{py: 3}}>
                                <CircularProgress size={24} />
                            </Stack>
                        )}

                        {!historyQuery.isLoading && history.length === 0 && (
                            <Alert severity="info">
                                아직 생성 요청 이력이 없습니다. 위 섹션에서 [AI 생성 요청] 으로 시작하세요.
                            </Alert>
                        )}

                        {finalizeError && (
                            <Alert severity="error">{finalizeError}</Alert>
                        )}

                        {history.map((req) => (
                            <RequestHistoryCard
                                key={req.requestId}
                                req={req}
                                selectedRequestId={selectedRequestId}
                                selectedResultId={selectedResultId}
                                onSelect={(requestId, resultId) => {
                                    setSelectedRequestId(requestId);
                                    setSelectedResultId(resultId);
                                }}
                                onFinalize={handleFinalize}
                                isFinalizing={finalizeMut.isPending}
                            />
                        ))}
                    </Stack>
                </Section>
            )}
        </Box>
    );
};
